import type { GetReportsResp, ReorderItem, Report, SetReportsSidebarLayoutResp } from '@/api/generated/model';

/** Default visible report count when seeding sidebar visibility for a project. */
export const REPORTS_SIDEBAR_VISIBLE = 10;

export const SIDEBAR_HIDDEN_REPORTS_STORAGE_KEY = 'nebuly_sidebar_hidden_reports';
export const SIDEBAR_REPORTS_ORDER_STORAGE_KEY = 'nebuly_sidebar_reports_order';

export type SidebarReportSectionKind = 'favorites' | 'workspace' | 'private';

/**
 * Sections whose arrangement belongs to the user. The workspace list is instead
 * stored on the report rows themselves so admins define it once for everyone.
 */
export type LocalSidebarReportSectionKind = Exclude<SidebarReportSectionKind, 'workspace'>;

/** Per-project sidebar hidden ids for favorites and private sections. */
export type HiddenSidebarReportsState = Record<string, { favorites?: string[]; private?: string[] }>;

/** Per-project sidebar order ids for favorites and private sections. */
export type SidebarReportsOrderState = Record<string, { favorites?: string[]; private?: string[] }>;

export function sortReportsForSidebar(a: Report, b: Report): number {
  const ta = a.created_at ? Date.parse(a.created_at) : 0;
  const tb = b.created_at ? Date.parse(b.created_at) : 0;
  if (tb !== ta) return tb - ta;
  return a.name.localeCompare(b.name);
}

/**
 * The workspace list is arranged entirely by the backend — this only reads the
 * position it assigned, highest first. `sidebar_order` is always set for an available
 * workspace report; a missing one can only mean a row the backend has not reached
 * yet, so it goes last rather than jumping the queue.
 */
export function sortWorkspaceReportsForSidebar(a: Report, b: Report): number {
  const oa = a.sidebar_order ?? null;
  const ob = b.sidebar_order ?? null;
  if (oa !== ob) {
    if (oa === null) return 1;
    if (ob === null) return -1;
    return ob - oa;
  }
  return sortReportsForSidebar(a, b);
}

/** Which workspace reports sit under "More". Decided by the backend, not here. */
export function getWorkspaceHiddenReportIds(workspaceReports: Report[]): Set<string> {
  return new Set(workspaceReports.filter((r) => r.sidebar_hidden === true).map((r) => r.id));
}

/** The workspace layout is written as a full snapshot of the list, top row first. */
export function buildWorkspaceSidebarLayoutPayload(
  orderedWorkspaceReports: Report[],
  hiddenReportIds: Set<string>,
  version: number,
): { order: ReorderItem[]; version: number } {
  return {
    version,
    order: orderedWorkspaceReports.map((r) => ({ id: r.id, enabled: !hiddenReportIds.has(r.id) })),
  };
}

const REPORT_LAYOUT_KEY_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function reportIdFromSidebarLayoutKey(key: string): string | null {
  if (key.startsWith('report:')) return key.slice('report:'.length);
  if (REPORT_LAYOUT_KEY_UUID_RE.test(key)) return key;
  return null;
}

/** Merge a successful sidebar-layout PUT into the reports query cache. */
export function applySidebarLayoutResponse(
  old: GetReportsResp | undefined,
  response: SetReportsSidebarLayoutResp,
): GetReportsResp | undefined {
  if (!old) return old;
  const byReportId = new Map<string, (typeof response.items)[number]>();
  for (const item of response.items) {
    const reportId = reportIdFromSidebarLayoutKey(item.key);
    if (!reportId) continue;
    byReportId.set(reportId, item);
  }
  return {
    ...old,
    sidebar_layout_version: response.version,
    reports: old.reports.map((report) => {
      const item = byReportId.get(report.id);
      if (!item) return report;
      return {
        ...report,
        sidebar_order: item.sidebar_order,
        sidebar_hidden: item.sidebar_hidden,
      };
    }),
  };
}

export function applyOptimisticWorkspaceLayoutToCache(
  old: GetReportsResp | undefined,
  orderedWorkspaceReports: Report[],
  hiddenReportIds: Set<string>,
): GetReportsResp | undefined {
  if (!old) return old;
  const { order } = buildWorkspaceSidebarLayoutPayload(
    orderedWorkspaceReports,
    hiddenReportIds,
    old.sidebar_layout_version ?? 0,
  );
  const positions = workspaceSidebarPositions(order);
  const enabledById = new Map(order.map((item) => [item.id, item.enabled]));
  return {
    ...old,
    reports: old.reports.map((report) =>
      positions.has(report.id)
        ? {
            ...report,
            sidebar_order: positions.get(report.id),
            sidebar_hidden: !enabledById.get(report.id),
          }
        : report,
    ),
  };
}

/**
 * The positions the backend will store for a snapshot, so an optimistic update can
 * show the final state instead of guessing. Mirrors `set_sidebar_layout`: top row
 * first, numbered downwards from `length - 1`.
 */
export function workspaceSidebarPositions(order: ReorderItem[]): Map<string, number> {
  const highest = order.length - 1;
  return new Map(order.map((item, rank) => [item.id, highest - rank]));
}

export function applyStoredOrder<T extends { id: string }>(items: T[], storedIds: string[] | undefined): T[] {
  if (!storedIds?.length) return items;
  const itemMap = new Map(items.map((i) => [i.id, i]));
  const ordered: T[] = [];
  for (const id of storedIds) {
    const item = itemMap.get(id);
    if (item) {
      ordered.push(item);
      itemMap.delete(id);
    }
  }
  for (const item of itemMap.values()) {
    ordered.push(item);
  }
  return ordered;
}

export function mergeFavoriteFlagsIntoReports(
  reports: Report[] | undefined,
  favoriteIds: Set<string>,
  favoritesLoaded: boolean,
): Report[] {
  return (reports ?? []).map((r) => ({
    ...r,
    is_favorite: favoriteIds.has(r.id) ? true : favoritesLoaded ? false : !!(r.is_favorite ?? false),
  }));
}

export function getDefaultHiddenReportIds(sectionReports: Report[]): Set<string> {
  return new Set(sectionReports.slice(REPORTS_SIDEBAR_VISIBLE).map((r) => r.id));
}

/** Favorites use custom order for default visibility; private uses creation sort only. */
export function getSectionReportsForVisibilityDefaults(
  section: LocalSidebarReportSectionKind,
  sectionReportsUnordered: Report[],
  sectionReportsOrdered: Report[],
): Report[] {
  return section === 'favorites' ? sectionReportsOrdered : sectionReportsUnordered;
}

export function getHiddenReportIdsForSection(
  hiddenState: HiddenSidebarReportsState,
  projectId: string | undefined,
  section: LocalSidebarReportSectionKind,
  sectionReportsForDefaults: Report[],
  favoriteNavPersistIds?: string[],
): Set<string> {
  const entry = projectId ? hiddenState[projectId] : undefined;
  const stored = entry?.[section];
  if (stored !== undefined) {
    const hidden = new Set(stored);
    if (section === 'favorites' && favoriteNavPersistIds !== undefined) {
      const knownIds = new Set([
        ...favoriteNavPersistIds,
        ...sectionReportsForDefaults.map((report) => report.id),
      ]);
      return new Set([...hidden].filter((id) => knownIds.has(id)));
    }
    return hidden;
  }
  if (section === 'favorites' && favoriteNavPersistIds !== undefined) {
    return new Set(favoriteNavPersistIds.slice(REPORTS_SIDEBAR_VISIBLE));
  }
  return getDefaultHiddenReportIds(sectionReportsForDefaults);
}

export function updateHiddenReportIdsForSection(
  hiddenState: HiddenSidebarReportsState,
  projectId: string,
  section: LocalSidebarReportSectionKind,
  sectionReportsForDefaults: Report[],
  reportId: string,
  visible: boolean,
  favoriteNavPersistIds?: string[],
): HiddenSidebarReportsState {
  const entry = hiddenState[projectId] ?? {};
  const currentHidden = getHiddenReportIdsForSection(
    hiddenState,
    projectId,
    section,
    sectionReportsForDefaults,
    favoriteNavPersistIds,
  );
  if (visible) currentHidden.delete(reportId);
  else currentHidden.add(reportId);
  return { ...hiddenState, [projectId]: { ...entry, [section]: Array.from(currentHidden) } };
}
